import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { shippingApi, repairApi, fuelReceiptApi, vehicleApi, shippingPriceApi, driverApi, getDeliveryNoteImageUrl } from '../services/api';
import { CreateShippingRecord, Driver } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import ImageViewer from '../components/ImageViewer';
import LinkRepairModal from '../components/shipping/LinkRepairModal';
import LinkFuelModal from '../components/shipping/LinkFuelModal';
import LinkPrepaidModal from '../components/shipping/LinkPrepaidModal';
import LinkedRecordsSection from '../components/shipping/LinkedRecordsSection';
import DeliveryNoteUploadSection from '../components/shippingRecord/DeliveryNoteUploadSection';
import ShippingRecordFormFields from '../components/shippingRecord/ShippingRecordFormFields';
import { useShippingFeeCalculations } from '../hooks/useShippingFeeCalculations';
import { useShippingRecordLinks } from '../hooks/useShippingRecordLinks';

const AddShippingRecordPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id?: string }>();
    const { t } = useLanguage();
    const numericId = id ? parseInt(id, 10) : undefined;
    const isEditMode = Boolean(numericId);
  
    // Mobile detection
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
  };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // State and hooks
    const [formData, setFormData] = useState<CreateShippingRecord>({
        customerName: '',
     customerPhone: '',
        customerEmail: '',
    shippingAddress: '',
        licensePlate: '',
        shippingDate: '',
        driverName: '',
 driverPhone: '',
        loadUnloadFee: 0,
        tollFee: 0,
        extraTollFee1: 0,
        extraTollFee2: 0,
        prepaidAmount: 0,
        shippingCost: 0,
  rm2Fee: 0,
        notes: '',
      vehicleId: undefined,
 provinceId: undefined,
     districtId: undefined,
        driverId: undefined,
 salaryRate: undefined,
  });
    const [deliveryNoteImage, setDeliveryNoteImage] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [deliveryNoteJson, setDeliveryNoteJson] = useState<any>(null);
    const [linkedRepairRecords, setLinkedRepairRecords] = useState<number[]>([]);
    const [linkedFuelReceipts, setLinkedFuelReceipts] = useState<number[]>([]);
    const [availableRepairRecords, setAvailableRepairRecords] = useState<any[]>([]);
    const [availableFuelReceipts, setAvailableFuelReceipts] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);
  const [provinces, setProvinces] = useState<any[]>([]);
    const [districts, setDistricts] = useState<any[]>([]);
    const [drivers, setDrivers] = useState<Driver[]>([]);
  const [pendingRecordId, setPendingRecordId] = useState<number | null>(null);
    const [pendingSaved, setPendingSaved] = useState(false);
    const [deliveryNoteImageUrl, setDeliveryNoteImageUrl] = useState<string | null>(null);
    const pendingDistrictId = useRef<number | undefined>(undefined);
    const [showImageModal, setShowImageModal] = useState(false);

    // Modal states
    const [showRepairLinkModal, setShowRepairLinkModal] = useState(false);
    const [showFuelLinkModal, setShowFuelLinkModal] = useState(false);
    const [showPrepaidLinkModal, setShowPrepaidLinkModal] = useState(false);
    const [showDateWarningModal, setShowDateWarningModal] = useState(false);
    const [dateWarningMessage, setDateWarningMessage] = useState('');
    const dateWarningResolveRef = useRef<((value: boolean) => void) | null>(null);

    // Use fee calculations hook
    const {
        calculateAllCosts,
        updateLoadUnloadFee,
        updateTollFee,
        updateShippingPrice,
        updateRM2Fee,
        updateSalaryRate
    } = useShippingFeeCalculations({ formData, setFormData, vehicles, drivers, isEditMode });

    // Use shipping record links hook
    const {
        currentlyLinkedRepairRecords,
        currentlyLinkedFuelReceipts,
     currentlyLinkedPrepaidRecords,
        availableUnlinkedRepairRecords,
        availableUnlinkedFuelReceipts,
        availableUnlinkedPrepaidRecords,
        handleLinkRepairRecord,
    handleLinkFuelReceipt,
        handleUnlinkRepairRecord,
   handleUnlinkFuelReceipt,
        handleLinkPrepaidRecord,
      handleUnlinkPrepaidRecord
    } = useShippingRecordLinks({ pendingRecordId, isEditMode, numericId });

    // Close modal on Esc key
    useEffect(() => {
        if (!showImageModal) return;
        const handleKeyDown = (e: KeyboardEvent) => {
          if (e.key === 'Escape') setShowImageModal(false);
 };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
}, [showImageModal]);

 // Fetch provinces and districts
    const fetchAvailableRecords = async () => {
        try {
         const [repairResponse, fuelResponse, vehicleResponse, provinceResponse] = await Promise.all([
          repairApi.getAll(),
       fuelReceiptApi.getAll(),
          vehicleApi.getAll(),
         shippingPriceApi.getProvinces()
    ]);
setAvailableRepairRecords(repairResponse.data.filter((r: any) => !r.shippingRecordId));
            setAvailableFuelReceipts(fuelResponse.data.filter((f: any) => !f.shippingRecordId));
      setVehicles(vehicleResponse.data);
     setProvinces(provinceResponse.data);
      } catch (error) {
          console.error('Error fetching linked records:', error);
        }
    };

    // Fetch districts when province changes
    useEffect(() => {
      if (formData.provinceId) {
            shippingPriceApi.getDistricts(formData.provinceId).then(res => {
  setDistricts(res.data);
                const found = res.data.find((d: any) => d.id === formData.districtId);
       if (!found && pendingDistrictId.current) {
       const pendingFound = res.data.find((d: any) => d.id === pendingDistrictId.current);
         if (pendingFound) {
             setFormData(prev => ({ ...prev, districtId: pendingDistrictId.current }));
 } else {
      setFormData(prev => ({ ...prev, districtId: undefined }));
 }
          pendingDistrictId.current = undefined;
 }
        });
        } else {
   setDistricts([]);
     setFormData(prev => ({ ...prev, districtId: undefined }));
            pendingDistrictId.current = undefined;
    }
    }, [formData.provinceId]);

    // Fetch available records on mount
    useEffect(() => {
        fetchAvailableRecords();
    }, []);

    // Fetch record for edit
    useEffect(() => {
        if (isEditMode && numericId) {
            (async () => {
        try {
     const response = await shippingApi.getById(numericId);
           const record = response.data;
          setFormData({
    customerName: record.customerName,
     customerPhone: record.customerPhone || '',
       customerEmail: record.customerEmail || '',
   shippingAddress: record.shippingAddress,
        licensePlate: record.licensePlate,
          shippingDate: record.shippingDate.split('T')[0],
      driverName: record.driverName,
           driverPhone: record.driverPhone || '',
         loadUnloadFee: record.loadUnloadFee,
     tollFee: record.tollFee,
             extraTollFee1: record.extraTollFee1,
    extraTollFee2: record.extraTollFee2,
          prepaidAmount: record.prepaidAmount,
   shippingCost: record.shippingCost,
       rm2Fee: record.rM2Fee,
     notes: record.notes || '',
       provinceId: record.provinceId,
       districtId: record.districtId,
         vehicleId: record.vehicleId,
   driverId: record.driverId,
          salaryRate: record.salaryRate
        });
        pendingDistrictId.current = record.districtId;
        setPendingRecordId(numericId);
     if (record.hasDeliveryNoteImage) {
     setDeliveryNoteImageUrl(getDeliveryNoteImageUrl(record.id));
           }
        } catch (error) {
        console.error('Error loading shipping record:', error);
       }
            })();
        }
    }, [isEditMode, numericId]);

    function formatDate(dateStr: string) {
        if (!dateStr) return '';
        const parts = dateStr.split('-');
      if (parts.length === 3) {
            let year = parts[0].length === 2 ? '20' + parts[0] : parts[0];
 return `${year}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
  }
        return dateStr;
    }

    function formatNumberWithDot(value: number): string {
        if (!value) return '0';
return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setDeliveryNoteImage(e.target.files[0]);
        setDeliveryNoteJson(null);
       setUploadError('');
   }
    };

    const handleUploadImage = async () => {
        setFormData({
          customerName: '',
     customerPhone: '',
    customerEmail: '',
shippingAddress: '',
   licensePlate: '',
      shippingDate: '',
     driverName: '',
            driverPhone: '',
          loadUnloadFee: 0,
        tollFee: 0,
  extraTollFee1: 0,
      extraTollFee2: 0,
    prepaidAmount: 0,
        shippingCost: 0,
     rm2Fee: 0,
          notes: '',
      provinceId: undefined,
         districtId: undefined,
        });
    setLinkedRepairRecords([]);
        setLinkedFuelReceipts([]);
 setUploading(true);
  setUploadError('');
        setDeliveryNoteJson(null);
   if (!deliveryNoteImage) {
         setUploading(false);
            setUploadError('No file selected');
      return;
        }
  try {
  const response = await shippingApi.processDeliveryNote(deliveryNoteImage);
            const enhancedResult = response.data;
            setDeliveryNoteJson(enhancedResult);
            if (enhancedResult.suggestedShippingRecord) {
          const suggested = enhancedResult.suggestedShippingRecord;
                const matches = enhancedResult.matches;
    setFormData(prev => ({
         ...prev,
              customerName: suggested.customerName || prev.customerName,
         customerPhone: suggested.customerPhone || prev.customerPhone,
      customerEmail: suggested.customerEmail || prev.customerEmail,
  shippingAddress: suggested.shippingAddress || prev.shippingAddress,
   licensePlate: matches?.vehicle?.licensePlate || prev.licensePlate,
        shippingDate: suggested.shippingDate ? new Date(suggested.shippingDate).toISOString().split('T')[0] : prev.shippingDate,
        driverName: suggested.driverName || prev.driverName,
           driverPhone: suggested.driverPhone || prev.driverPhone,
    notes: suggested.notes || prev.notes,
 vehicleId: matches?.vehicle?.id || prev.vehicleId,
       driverId: suggested.driverId || matches?.driver?.id || prev.driverId,
     provinceId: matches?.address?.province?.id || prev.provinceId,
           districtId: matches?.address?.district?.id || prev.districtId
        }));
    }
   } catch (err: any) {
            if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
                setUploadError(t('request.timeout.message') || 'Request timeout: Image processing is taking longer than expected. Please try a smaller image or try again later.');
      } else if (err.response?.status === 504) {
    setUploadError(t('gateway.timeout.message') || 'Gateway timeout: The server is taking too long to process the image. Please try again with a smaller image or contact support if the issue persists.');
            } else {
                setUploadError((err as any).message || 'Failed to process image');
}
        } finally {
         setUploading(false);
        }
  };

    useEffect(() => {
        if (deliveryNoteJson) {
      setFormData((prev) => ({
           ...prev,
   customerName: deliveryNoteJson.recipient_info?.company_name || prev.customerName,
     customerPhone: deliveryNoteJson.recipient_info?.phone || prev.customerPhone,
      customerEmail: deliveryNoteJson.recipient_info?.email || prev.customerEmail,
      shippingAddress: deliveryNoteJson.recipient_info?.address || prev.shippingAddress,
 licensePlate: deliveryNoteJson.recipient_info?.vehicle_number || prev.licensePlate,
        shippingDate: deliveryNoteJson.issue_date ? formatDate(deliveryNoteJson.issue_date) : prev.shippingDate,
                driverName: deliveryNoteJson.signatures?.deliverer?.name || prev.driverName,
        notes: deliveryNoteJson.instructions_to_customer || prev.notes,
            }));
        }
    }, [deliveryNoteJson]);

    // Fetch drivers on mount
    useEffect(() => {
        driverApi.getAll().then(res => setDrivers(res.data)).catch(() => setDrivers([]));
    }, []);

    // Fetch salary rate when driver changes (add mode only)
    useEffect(() => {
        if (!isEditMode && formData.driverId && formData.shippingDate) {
    driverApi.getDriverSalaryRate(formData.driverId, formData.shippingDate)
       .then(response => {
         setFormData(prev => ({ ...prev, salaryRate: response.data.salaryRate }));
    })
     .catch(error => {
        console.error('Error fetching salary rate:', error);
     });
    }
    }, [formData.driverId, formData.shippingDate, isEditMode]);

    const isShippingDateSuspicious = (): boolean => {
        if (!formData.shippingDate) return false;
        const shippingDate = new Date(formData.shippingDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        shippingDate.setHours(0, 0, 0, 0);
        const diffMs = Math.abs(shippingDate.getTime() - today.getTime());
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        return diffDays > 1;
    };

    const confirmShippingDate = (): Promise<boolean> => {
        if (!isShippingDateSuspicious()) return Promise.resolve(true);
        const today = new Date().toISOString().split('T')[0];
        const message = (t('shipping.date.warning') || 'Warning: The shipping date ({shippingDate}) differs from today ({today}) by more than 1 day. AI may have read the date incorrectly from the image. Are you sure the shipping date is correct?')
            .replace('{shippingDate}', formData.shippingDate)
            .replace('{today}', today);
        setDateWarningMessage(message);
        setShowDateWarningModal(true);
        return new Promise<boolean>((resolve) => {
            dateWarningResolveRef.current = resolve;
        });
    };

    const handleDateWarningConfirm = () => {
        setShowDateWarningModal(false);
        dateWarningResolveRef.current?.(true);
        dateWarningResolveRef.current = null;
    };

    const handleDateWarningCancel = () => {
        setShowDateWarningModal(false);
        dateWarningResolveRef.current?.(false);
        dateWarningResolveRef.current = null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!(await confirmShippingDate())) return;
  try {
    let shippingRecordId: number;
      if (isEditMode && numericId) {
      await shippingApi.update(numericId, formData);
    shippingRecordId = numericId;
      } else {
             const response = await shippingApi.create(formData);
       shippingRecordId = response.data.id;
  }
  for (const repairId of linkedRepairRecords) {
    const repair = availableRepairRecords.find(r => r.id === repairId);
                if (repair) await repairApi.update(repairId, { ...repair, shippingRecordId });
       }
        for (const fuelId of linkedFuelReceipts) {
                const fuel = availableFuelReceipts.find(f => f.id === fuelId);
     if (fuel) await fuelReceiptApi.update(fuelId, { ...fuel, shippingRecordId });
         }
     navigate('/shipping');
   } catch (error) {
            console.error('Error saving shipping record:', error);
        }
};

    const handleSavePending = async () => {
        try {
        const data = new FormData();
     Object.entries(formData).forEach(([key, value]) => {
if (value !== undefined && value !== null) data.append(key, value as any);
            });
            data.append('status', 'pending');
            if (deliveryNoteImage) data.append('DeliveryNoteImage', deliveryNoteImage);
            const response = await shippingApi.createWithImage(data);
  const result = response.data;
   setPendingRecordId(result.id);
     setPendingSaved(true);
            setDeliveryNoteImageUrl(getDeliveryNoteImageUrl(result.id));
        } catch (err) {
      setUploadError((err as any).message || 'Failed to save record');
     }
    };

    const handleFinalize = async () => {
        if (!pendingRecordId) return;
     try {
      const data = { ...formData, status: 'created' };
   await shippingApi.update(pendingRecordId, data);
         navigate('/shipping');
     } catch (err) {
     setUploadError((err as any).message || 'Failed to finalize record');
  }
    };

    const handleSuggestion = useCallback((suggested: any, matches: any) => {
        setFormData(prev => {
 let updatedFormData: any = {
        ...prev,
    provinceId: matches?.address?.province?.id || prev.provinceId,
            districtId: matches?.address?.district?.id || prev.districtId,
       driverId: suggested?.driverId || matches?.driver?.id || prev.driverId
          };
if (matches?.vehicle?.licensePlate && typeof matches?.vehicle?.licensePlate === 'string') {
      const matchedVehicle = vehicles.find(v => v.licensePlate && v.licensePlate.toLowerCase() === matches.vehicle.licensePlate.toLowerCase());
       if (matchedVehicle) {
        updatedFormData = {
    ...updatedFormData,
           vehicleId: matchedVehicle.id,
          licensePlate: matchedVehicle.licensePlate
      };
     }
   }
         return updatedFormData;
    });
 }, [vehicles]);

    useEffect(() => {
 if (deliveryNoteJson?.suggestedShippingRecord) {
     const suggested = deliveryNoteJson.suggestedShippingRecord;
     const matches = deliveryNoteJson.matches;
            handleSuggestion(suggested, matches);
        }
    }, [deliveryNoteJson, handleSuggestion]);

    const [recordStatus, setRecordStatus] = useState<string>('');
    useEffect(() => {
  if (isEditMode && numericId) {
            (async () => {
    try {
            const response = await shippingApi.getById(numericId);
 setRecordStatus((response.data as any).status || '');
 } catch {}
 })();
     }
    }, [isEditMode, numericId]);

    const handleMarkAsCreated = async () => {
        if (!(await confirmShippingDate())) return;
        try {
            await shippingApi.update(numericId!, { ...(formData as any), status: 'created' });
       navigate('/shipping');
        } catch (error) {
  alert('Failed to mark as created.');
        }
    };

    return (
        <div className="container" style={{ 
            padding: isMobile ? '12px' : '24px',
  maxWidth: isMobile ? '100%' : '1400px'
        }}>
<div className="row">
                {/* Main Content - Full width on mobile, 8 cols on desktop */}
         <div className="col-12 col-lg-8 order-lg-1 order-1">
      {/* Back button */}
          {isEditMode && (
         <button
                   type="button"
              className="btn btn-link px-0"
           style={{ 
        marginBottom: isMobile ? '12px' : '16px',
        fontSize: isMobile ? '14px' : '16px'
         }}
          onClick={() => navigate('/shipping')}
       >
    ← {t('back.to.shipping.records')}
             </button>
   )}
       
             {/* Page Title */}
       <h2 style={{ 
    marginBottom: isMobile ? '16px' : '24px',
    fontSize: isMobile ? '20px' : '28px'
                    }}>
       {isEditMode ? t('edit.shipping.record') : t('add.shipping.record')}
           </h2>
        
   <form onSubmit={handleSubmit}>
   {/* Delivery Note Upload Section */}
        <DeliveryNoteUploadSection
        t={t}
        isEditMode={isEditMode}
       deliveryNoteImageUrl={deliveryNoteImageUrl}
    onViewImage={() => setShowImageModal(true)}
           deliveryNoteImage={deliveryNoteImage}
    uploading={uploading}
            pendingSaved={pendingSaved}
             uploadError={uploadError}
      deliveryNoteJson={deliveryNoteJson}
  onImageChange={handleImageChange}
          onUploadImage={handleUploadImage}
 onSavePending={handleSavePending}
         onFinalize={handleFinalize}
            onCancel={() => navigate('/shipping')}
   />

{/* All form fields */}
             <ShippingRecordFormFields
      t={t}
      formData={formData}
           setFormData={setFormData}
      provinces={provinces}
       districts={districts}
       vehicles={vehicles}
   drivers={drivers}
               updateLoadUnloadFee={updateLoadUnloadFee}
             updateShippingPrice={updateShippingPrice}
   updateTollFee={updateTollFee}
        updateRM2Fee={updateRM2Fee}
       updateSalaryRate={updateSalaryRate}
     formatNumberWithDot={formatNumberWithDot}
        onCalculateAllCosts={calculateAllCosts}
        />
             
    {/* Linked Records Section */}
        <LinkedRecordsSection
         repairRecords={currentlyLinkedRepairRecords}
      fuelReceipts={currentlyLinkedFuelReceipts}
         prepaidRecords={currentlyLinkedPrepaidRecords}
         pendingRecordId={pendingRecordId}
 isEditMode={isEditMode}
        numericId={numericId}
    onUnlinkRepair={handleUnlinkRepairRecord}
          onUnlinkFuel={handleUnlinkFuelReceipt}
  onUnlinkPrepaid={handleUnlinkPrepaidRecord}
        onShowRepairModal={() => setShowRepairLinkModal(true)}
        onShowFuelModal={() => setShowFuelLinkModal(true)}
              onShowPrepaidModal={() => setShowPrepaidLinkModal(true)}
    onSetPendingRecordId={setPendingRecordId}
       />
  
             {/* Action Buttons - Properly Aligned */}
          <div className="row mb-3">
               <div className="col-md-3"></div>
          <div className="col-md-9">
  {(!pendingSaved || isEditMode) && !(deliveryNoteJson && !pendingSaved) && (
       <div className={isMobile ? 'd-grid gap-2' : 'd-flex gap-2'}>
       <button 
  type="submit" 
    className="btn btn-primary"
   style={{ fontSize: isMobile ? '14px' : '16px' }}
       >
           {isEditMode ? t('update') + ' ' + t('shipping.record') : t('create') + ' ' + t('shipping.record')}
         </button>
       
             {!(deliveryNoteJson && !pendingSaved) && (
     <button 
  type="button" 
      className="btn btn-secondary" 
     onClick={() => navigate('/shipping')}
    style={{ fontSize: isMobile ? '14px' : '16px' }}
          >
    {t('cancel')}
     </button>
   )}
       
            {/* Mark as Created button - Same row in edit mode for pending records */}
        {isEditMode && recordStatus === 'pending' && (
    <button 
      type="button" 
    className="btn btn-success"
    style={{ fontSize: isMobile ? '14px' : '16px' }}
  onClick={handleMarkAsCreated}
   >
    {t('mark.as.created') || 'Mark as Created'}
 </button>
    )}
        </div>
             )}
        </div>
        </div>
      </form>
   </div>
      
       {/* Right sidebar - Hidden on mobile, visible on desktop */}
             <div className="col-lg-4 order-lg-2 d-none d-lg-block">
               {/* Future content can go here */}
             </div>
            </div>
    
     {/* Enhanced Image Viewer */}
            <ImageViewer
     src={deliveryNoteImageUrl || ''}
          alt="Delivery Note"
       isOpen={showImageModal}
    onClose={() => setShowImageModal(false)}
            />

          {/* Link Modals - Responsive */}
  <LinkRepairModal
          isOpen={showRepairLinkModal}
     onClose={() => setShowRepairLinkModal(false)}
                availableRecords={availableUnlinkedRepairRecords}
          onLink={(id) => {
   handleLinkRepairRecord(id);
    setShowRepairLinkModal(false);
}}
       />
     <LinkFuelModal
     isOpen={showFuelLinkModal}
        onClose={() => setShowFuelLinkModal(false)}
        availableRecords={availableUnlinkedFuelReceipts}
    onLink={(id) => {
        handleLinkFuelReceipt(id);
          setShowFuelLinkModal(false);
         }}
            />
            <LinkPrepaidModal
                isOpen={showPrepaidLinkModal}
         onClose={() => setShowPrepaidLinkModal(false)}
      availableRecords={availableUnlinkedPrepaidRecords}
     onLink={(id) => {
         handleLinkPrepaidRecord(id);
        setShowPrepaidLinkModal(false);
       }}
   />

            {/* Shipping Date Warning Modal */}
            {showDateWarningModal && (
                <div className="modal show d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-header bg-warning bg-opacity-10 border-warning">
                                <h5 className="modal-title d-flex align-items-center gap-2 text-warning">
                                    <i className="bi bi-exclamation-triangle-fill"></i>
                                    {t('shipping.date.warning.title') || 'Shipping Date Warning'}
                                </h5>
                                <button type="button" className="btn-close" onClick={handleDateWarningCancel}></button>
                            </div>
                            <div className="modal-body py-4">
                                <div className="d-flex align-items-start gap-3">
                                    <div className="bg-warning bg-opacity-10 rounded-circle p-2 flex-shrink-0" style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <i className="bi bi-robot text-warning" style={{ fontSize: '1.4rem' }}></i>
                                    </div>
                                    <div>
                                        <p className="mb-2" style={{ fontSize: '0.95rem', lineHeight: 1.6 }}>{dateWarningMessage}</p>
                                        <div className="d-flex gap-3 mt-3">
                                            <div className="text-center">
                                                <small className="text-muted d-block">{t('shipping.date') || 'Shipping Date'}</small>
                                                <span className="badge bg-danger bg-opacity-10 text-danger px-3 py-2" style={{ fontSize: '0.9rem' }}>
                                                    {formData.shippingDate}
                                                </span>
                                            </div>
                                            <div className="text-center">
                                                <small className="text-muted d-block">{t('today') || 'Today'}</small>
                                                <span className="badge bg-success bg-opacity-10 text-success px-3 py-2" style={{ fontSize: '0.9rem' }}>
                                                    {new Date().toISOString().split('T')[0]}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer border-0 pt-0">
                                <button type="button" className="btn btn-outline-secondary" onClick={handleDateWarningCancel}>
                                    <i className="bi bi-x-lg me-1"></i>
                                    {t('cancel') || 'Cancel'}
                                </button>
                                <button type="button" className="btn btn-warning" onClick={handleDateWarningConfirm}>
                                    <i className="bi bi-check-lg me-1"></i>
                                    {t('shipping.date.confirm') || 'Yes, Date is Correct'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddShippingRecordPage;